/**
 * ReconnectingBanner
 *
 * Shows a fixed banner when the app is reconnecting after tab wake.
 * - "Reconnecting..." with spinner after 1s delay
 * - "Connection lost. Tap to reload." after 10s timeout
 */

import { useState, useEffect, useRef } from 'react';
import { useVisibilityReconnect } from '@/providers/VisibilityReconnectProvider';

export function ReconnectingBanner() {
  const { reconnectState } = useVisibilityReconnect();
  const [showBanner, setShowBanner] = useState(false);
  const delayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (reconnectState === 'reconnecting') {
      // Show banner after 1s delay
      delayRef.current = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
    } else if (reconnectState === 'failed') {
      // Show failed state immediately
      setShowBanner(true);
    } else {
      // Idle: hide
      setShowBanner(false);
      if (delayRef.current) {
        clearTimeout(delayRef.current);
        delayRef.current = null;
      }
    }

    return () => {
      if (delayRef.current) {
        clearTimeout(delayRef.current);
        delayRef.current = null;
      }
    };
  }, [reconnectState]);

  if (!showBanner) return null;

  const isFailed = reconnectState === 'failed';

  return (
    <div
      className="fixed top-0 left-0 md:left-52 right-0 z-[60] flex items-center justify-center transition-transform duration-300 h-[40px] bg-card border-b border-border"
    >
      {isFailed ? (
        <span className="text-sm" style={{ color: '#888888' }}>
          Connection lost.{' '}
          <button
            onClick={() => window.location.reload()}
            className="underline cursor-pointer"
            style={{ color: '#CCFF00' }}
          >
            Tap to reload
          </button>
          .
        </span>
      ) : (
        <span className="flex items-center gap-2 text-sm" style={{ color: '#888888' }}>
          <span
            className="inline-block w-2 h-2 rounded-full animate-pulse"
            style={{ background: '#CCFF00' }}
          />
          Reconnecting...
        </span>
      )}
    </div>
  );
}
